import { Building2, DollarSign, GitBranch, Hammer, Home, Layers3, Wrench } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import type { Deal, Project, PropertyCard } from '../lib/operatorWorkspace'
import { cn } from '../lib/utils'

type ModuleId = 'deals-pipeline' | 'properties' | 'construction'

type OperatorDashboardProps = {
  userName: string
  deals: Deal[]
  properties: PropertyCard[]
  projects: Project[]
  onOpenModule: (module: ModuleId) => void
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

export default function OperatorDashboardModule({ userName, deals, properties, projects, onOpenModule }: OperatorDashboardProps) {
  const pipelineValue = deals.reduce((sum, deal) => sum + deal.value, 0)
  const portfolioValue = properties.reduce((sum, property) => sum + property.value, 0)
  const activeProjects = projects.filter((project) => project.status === 'In Progress').length
  const pendingInvoices = projects.flatMap((project) => project.phases).filter((phase) => phase.invoiceStatus === 'Pending Review').length
  const queuedProperties = properties.filter((property) => property.lane === 'acquisition').length
  const stabilizedProperties = properties.filter((property) => property.lane === 'stabilized').length
  const estimateProjects = projects.filter((project) => project.status === 'Estimate').length
  const blockedPhases = projects.flatMap((project) => project.phases).filter((phase) => phase.status === 'Blocked').length
  const closingSoonDeals = deals.filter((deal) => deal.stage === 'contract' || deal.stage === 'offer').length
  const firstName = userName.trim().split(/\s+/)[0] || 'Operator'

  const attentionItems = [
    `${closingSoonDeals} deals are in offer or contract stages`,
    `${queuedProperties} properties still need to move out of acquisition`,
    `${estimateProjects} construction projects are still in estimate workflow`,
    `${pendingInvoices} invoices are waiting for review or payment`,
    `${blockedPhases} construction phases are currently blocked`,
  ]

  const moduleCards = [
    {
      id: 'deals-pipeline' as ModuleId,
      title: 'Deals Pipeline',
      icon: GitBranch,
      summary: `${deals.length} active deals`,
      detail: `${closingSoonDeals} near close`,
      accent: 'border-blue-200 bg-blue-50/70 dark:border-blue-900/60 dark:bg-blue-950/20',
    },
    {
      id: 'properties' as ModuleId,
      title: 'Property Management',
      icon: Home,
      summary: `${properties.length} tracked properties`,
      detail: `${stabilizedProperties} stabilized`,
      accent: 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/60 dark:bg-emerald-950/20',
    },
    {
      id: 'construction' as ModuleId,
      title: 'Construction',
      icon: Hammer,
      summary: `${projects.length} projects`,
      detail: `${activeProjects} in progress`,
      accent: 'border-amber-200 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/20',
    },
  ]

  return (
    <div className="space-y-4" data-testid="operator-dashboard">
      <Card className="border-slate-200 bg-gradient-to-r from-white via-slate-50 to-emerald-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/20">
        <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-300">Single Pane Of Glass</p>
            <h1 className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{firstName}'s Operator Dashboard</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Track BRRR deals, construction execution, and property lifecycle from one command surface.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200" onClick={() => onOpenModule('deals-pipeline')}>Open Deals</Button>
            <Button size="sm" variant="secondary" onClick={() => onOpenModule('properties')}>Open Properties</Button>
            <Button size="sm" variant="secondary" onClick={() => onOpenModule('construction')}>Open Construction</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
        {[
          { key: 'pipeline-value', label: 'Pipeline Value', value: fmt(pipelineValue), icon: DollarSign, tone: 'text-blue-700 dark:text-blue-400' },
          { key: 'portfolio-value', label: 'Portfolio Value', value: fmt(portfolioValue), icon: Building2, tone: 'text-emerald-700 dark:text-emerald-400' },
          { key: 'active-projects', label: 'Active Projects', value: String(activeProjects), icon: Hammer, tone: 'text-amber-700 dark:text-amber-400' },
          { key: 'pending-invoices', label: 'Pending Invoices', value: String(pendingInvoices), icon: Wrench, tone: 'text-rose-700 dark:text-rose-400' },
          { key: 'queued-acquisitions', label: 'Queued Acquisitions', value: String(queuedProperties), icon: Layers3, tone: 'text-violet-700 dark:text-violet-400' },
          { key: 'stabilized-assets', label: 'Stabilized Assets', value: String(stabilizedProperties), icon: Home, tone: 'text-slate-900 dark:text-slate-100' },
        ].map((item) => {
          const Icon = item.icon
          return (
            <Card key={item.key} className="p-3 shadow-sm" data-testid={`dashboard-kpi-${item.key}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{item.label}</p>
                <Icon className={cn('h-4 w-4', item.tone)} />
              </div>
              <p className={cn('mt-2 text-xl font-bold', item.tone)}>{item.value}</p>
            </Card>
          )
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Operations At A Glance</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            {moduleCards.map((item) => {
              const Icon = item.icon
              return (
                <div key={item.id} className={cn('rounded-2xl border p-3', item.accent)}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.title}</p>
                    <Icon className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                  </div>
                  <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">{item.summary}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{item.detail}</p>
                  <Button size="sm" variant="secondary" className="mt-3 h-7 px-2.5 text-xs" onClick={() => onOpenModule(item.id)}>Open board</Button>
                </div>
              )
            })}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Attention Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {attentionItems.map((item) => (
              <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                {item}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}